import { Badge, Paper, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';

import { DateBadgeIcon, DateBadgeIcons, DateBadgeProps, DateBadgeVariants } from './DateBadge.interface';

import AlertExclamationCircleFilledIcon from 'modules/common/components/Icons/AlertExclamationCircleFilledIcon';
import PendingClockIcon from 'modules/common/components/Icons/PendingClockIcon';

const StyledBadge = styled(Badge)({
    '& .MuiBadge-badge': {
        transform: 'translate(35%, -35%)',
        padding: 0,
    },
});

function OverlayIcon({ type }: Readonly<{ type: DateBadgeIcon }>) {
    const isUrgent = type === DateBadgeIcons.URGENT;
    return (
        <Stack
            alignItems="center"
            justifyContent="center"
            aria-label={isUrgent ? 'Urgent' : 'Due soon'}
            sx={{
                width: isUrgent ? 19 : 20,
                height: isUrgent ? 19 : 20,
                borderRadius: '50%',
                bgcolor: isUrgent ? 'background.paper' : 'grey.100',
            }}>
            {isUrgent ? (
                <AlertExclamationCircleFilledIcon color="error" sx={{ fontSize: 25 }} />
            ) : (
                <PendingClockIcon sx={{ fontSize: 16, color: 'grey.500' }} />
            )}
        </Stack>
    );
}

export default function DateBadge({ date, icon, variant = DateBadgeVariants.OUTLINE }: Readonly<DateBadgeProps>) {
    const parsedDate = dayjs(date);
    const month = parsedDate.format('MMM').toUpperCase();
    const day = parsedDate.format('DD');
    const isDropShadow = variant === DateBadgeVariants.DROP_SHADOW;

    return (
        <StyledBadge
            overlap="rectangular"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            badgeContent={icon ? <OverlayIcon type={icon} /> : null}
            invisible={!icon}>
            <Paper
                variant={isDropShadow ? 'elevation' : 'outlined'}
                sx={{
                    width: 48,
                    height: 48,
                    boxShadow: isDropShadow ? 9 : 'none',
                    transition: 'box-shadow 0.15s',
                }}>
                <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                    <Typography variant="overline" color="error">
                        {month}
                    </Typography>
                    <Typography variant="h3">{day}</Typography>
                </Stack>
            </Paper>
        </StyledBadge>
    );
}
